var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FormControlMixin } from '@open-wc/form-control';
import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { reset } from '../styles';
import { styles } from './styles';
export class WCheckbox extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /** The name of the checkbox, submitted as a name/value pair with form data. */
        this.name = '';
        /** The value of the checkbox, submitted as a name/value pair with form data. */
        this.value = null;
        /** Draws the checkbox in an indeterminate state. */
        this.indeterminate = false;
        /** Draws the checkbox in a checked state (reflected to attribute). */
        this.checked = false;
        /** Disables the checkbox. */
        this.disabled = false;
        /** Makes the checkbox a required field. */
        this.required = false;
        /** Draws the checkbox in an invalid state. */
        this.invalid = false;
        /** The default value of the form control. Used for resetting. */
        this.#defaultChecked = false;
        // Track whether invalid state was set by required validation.
        this.#invalidFromRequired = false;
        // Track whether the user has interacted with the checkbox.
        this.#hasInteracted = false;
        // Track whether tabindex was set automatically.
        this.#autoTabIndex = false;
        /* @internal */
        this.#handleInvalid = () => {
            this.#markInteracted();
            this.#updateValidity();
        };
        /* @internal */
        this.#handleKeyDown = (event) => {
            if (this.disabled)
                return;
            if (event.defaultPrevented)
                return;
            if (event.key !== ' ' && event.key !== 'Spacebar' && event.key !== 'Enter')
                return;
            if (event.composedPath()[0] === this.input)
                return;
            event.preventDefault();
            this.click();
        };
    }
    static { this.styles = [reset, styles]; }
    static { this.shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }; }
    /** The default value of the form control. Used for resetting. */
    #defaultChecked;
    // Track whether invalid state was set by required validation.
    #invalidFromRequired;
    // Track whether the user has interacted with the checkbox.
    #hasInteracted;
    // Track whether tabindex was set automatically.
    #autoTabIndex;
    connectedCallback() {
        super.connectedCallback();
        // kept for compat with old shared styling approach
        this.setAttribute('type', 'checkbox');
        const attrValue = this.getAttribute('value');
        this.value = attrValue ?? 'on';
        this.#defaultChecked = this.hasAttribute('checked');
        this.checked = this.#defaultChecked;
        this.#syncTabIndex();
        this.addEventListener('invalid', this.#handleInvalid);
        this.addEventListener('keydown', this.#handleKeyDown);
        this.#syncFormValue();
    }
    /* @internal */
    handleClick() {
        if (this.disabled)
            return;
        this.#markInteracted();
        this.checked = !this.checked;
        this.indeterminate = false;
        this.updateComplete.then(() => {
            this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        });
    }
    /* @internal */
    #handleInvalid;
    /* @internal */
    #handleKeyDown;
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('checked') || changedProperties.has('indeterminate')) {
            this.#syncInputState();
        }
        if (this.#shouldSyncFormState(changedProperties)) {
            this.#syncFormValue();
            this.#updateValidity();
        }
        if (changedProperties.has('disabled')) {
            this.#syncTabIndex();
        }
    }
    resetFormControl() {
        this.checked = this.#defaultChecked;
        this.#syncFormValue();
        this.#updateValidity();
    }
    /* @internal */
    click() {
        if (this.disabled)
            return;
        this.input?.click();
    }
    /* @internal */
    focus(options) {
        this.input?.focus(options);
    }
    /* @internal */
    blur() {
        this.input?.blur();
    }
    /** Returns the validation message if the checkbox is invalid, otherwise an empty string */
    get validationMessage() {
        return this.internals.validationMessage;
    }
    /** Returns the validity state of the checkbox */
    get validity() {
        return this.internals.validity;
    }
    /** Checks whether the checkbox passes constraint validation */
    checkValidity() {
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Checks validity and shows the browser's validation message if invalid */
    reportValidity() {
        this.#markInteracted();
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** @internal */
    #markInteracted() {
        this.#hasInteracted = true;
    }
    /** @internal */
    #getValidityMessage() {
        // Use a non-empty message to avoid native popovers while satisfying ElementInternals.
        return this.input?.validationMessage || ' ';
    }
    /** @internal */
    #getValidityAnchor() {
        return this.input ?? undefined;
    }
    /** @internal */
    #setInvalidState(state) {
        this.internals.setValidity(state, this.#getValidityMessage(), this.#getValidityAnchor());
    }
    /** @internal */
    #updateValidity() {
        if (this.disabled) {
            this.internals.setValidity({});
            return;
        }
        const requiredInvalid = this.required && !this.checked;
        // this means that invalid was set via the invalid attribute, not by required validation
        const externalInvalid = this.invalid && !this.#invalidFromRequired;
        if (requiredInvalid) {
            this.#invalidFromRequired = true;
            this.invalid = this.#hasInteracted;
            this.#setInvalidState({ valueMissing: true });
            return;
        }
        if (this.#invalidFromRequired) {
            this.invalid = false;
            this.#invalidFromRequired = false;
        }
        if (externalInvalid) {
            this.#setInvalidState({ customError: true });
            return;
        }
        this.internals.setValidity({});
    }
    /** @internal */
    #syncFormValue() {
        if (this.disabled) {
            this.setValue(null);
            return;
        }
        const value = this.checked ? this.value : null;
        this.setValue(value);
    }
    /** @internal */
    #syncInputState() {
        if (!this.input)
            return;
        this.input.checked = this.checked;
        this.input.indeterminate = this.indeterminate;
    }
    /** @internal */
    #syncTabIndex() {
        const hasTabIndexAttr = this.hasAttribute('tabindex');
        if (hasTabIndexAttr && !this.#autoTabIndex)
            return;
        this.tabIndex = this.disabled ? -1 : 0;
        this.#autoTabIndex = true;
    }
    /** @internal */
    #shouldSyncFormState(changedProperties) {
        return (changedProperties.has('checked') ||
            changedProperties.has('value') ||
            changedProperties.has('disabled') ||
            changedProperties.has('required') ||
            changedProperties.has('invalid'));
    }
    render() {
        const isIndeterminate = !this.checked && this.indeterminate;
        const ariaChecked = isIndeterminate ? 'mixed' : this.checked ? 'true' : 'false';
        return html `
      <label part="base" class="wrapper">
        <span part="control" class="checkbox control">
          <input
            part="input"
            class="input hide-toggle"
            type="checkbox"
            name=${this.name}
            value=${ifDefined(this.value)}
            .indeterminate=${live(this.indeterminate)}
            .checked=${live(this.checked)}
            .disabled=${this.disabled}
            .required=${this.required}
            aria-checked=${ariaChecked}
            aria-invalid=${ifDefined(this.invalid ? 'true' : undefined)}
            @click=${this.handleClick} />
          ${isIndeterminate ? '–' : ''}
        </span>

        <slot part="label"></slot>
      </label>
    `;
    }
}
__decorate([
    query('input[type="checkbox"]')
], WCheckbox.prototype, "input", void 0);
__decorate([
    property({ reflect: true })
], WCheckbox.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], WCheckbox.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WCheckbox.prototype, "indeterminate", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WCheckbox.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WCheckbox.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WCheckbox.prototype, "invalid", void 0);
if (!customElements.get('w-checkbox')) {
    customElements.define('w-checkbox', WCheckbox);
}
//# sourceMappingURL=checkbox.js.map